/**
 * 작성자 : 홍선영
 * 날짜 : 2023.08.04
 * 기능 : 현장설정관리-메인화면관리-DashBoard설정 페이지
 */

import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { IoCloseCircleSharp } from 'react-icons/io5';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { ButtonsWrapper, Root, DraggableCellStyle, DroppableCellStyle } from '../../../assets/styles/DashboardSettingStyle';

import { themeState, userState } from '../../../atoms';
import { DASHBOARD_GUBUN, DASHBOARD_TYPE_LIST } from '../../../_constants';
import { IComCd, IComCdList, IDashboard, IDashboardM } from 'customTypes';
import { BtnBlue } from '../../../components/Button';
import { arraySortByAscdOrder } from '../../../utils/arraySortByAscdOrder';
import { logPost } from '../../../services/log';
import { dark, light } from '../../../assets/styles/theme';
import { useSetAuth } from '../../../utils/useSetAuth';
import { apiGet, apiPost } from '../../../services/_common';
import GetFetchStatus from '../../../hooks/GetFetchStatus';

interface DraggableCellProps {
  cell: any;
  dragPieces: any;
  setDragPieces: any;
}

interface Piece {
  dCd: string;
  name: string;
  width: number;
  height: number;
  isInDropZone: boolean;
  location: { column: number; row: number; mRow?: number };
  cdSort: number;
  category: string;
}

interface DropItem {
  type: string;
  item: Piece;
}

const DraggableCell = ({ cell, dragPieces, setDragPieces }: DraggableCellProps) => {
  const { t } = useTranslation();
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'cell',
    item: cell,
    end: (item: any, monitor: any) => {
      // if (monitor.didDrop() && item.isInGrid && onRemoveFromGrid) {
      //   onRemoveFromGrid(item); // Call the function to remove the piece from the grid
      // }
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  useEffect(() => {
    if (isDragging) {
      const copyDragPieces = [...dragPieces];
      // cancelPiece의 id와 일치하는 항목을 드래그 피스 배열에서 찾기
      const index = copyDragPieces.findIndex((piece) => piece.dCd === cell.dCd);
      // 일치하는 아이템이 있으면 드래그피스 배열 업데이트
      if (index !== -1) {
        // cancelPiece의 속성을 사용하여 새 항목을 생성하고 InDropZone을 false 설정
        const updatedItem = {
          ...copyDragPieces[index],
          ...cell,
          isInDropZone: false,
        };

        // 업데이트된 아이템으로 드래그피스 배열의 아이템 대치
        copyDragPieces[index] = updatedItem;
        setDragPieces(copyDragPieces); // 드래그피스배열 setState
      }
    }
  }, [isDragging]);

  return (
    <DraggableCellStyle ref={drag} isDragging={isDragging}>
      {`${t(cell.name)} (${t('가로')}${cell.width} / ${t('세로')}${cell.height})`}
    </DraggableCellStyle>
  );
};

const DroppableZone = ({ children, onDrop, cell, dragPieces, setDragPieces, dropZoneGrid, setDropZoneGrid, auth }: any) => {
  // const { auth } = useSetAuth();
  const theme = useRecoilValue(themeState);
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'cell',
    drop: (item, monitor) => {
      onDrop(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  const onClickRemove = (cancelPiece: any) => {
    const copyDragPieces = [...dragPieces];
    // cancelPiece의 id와 일치하는 항목을 드래그 피스 배열에서 찾기
    const index = copyDragPieces.findIndex((piece) => piece.dCd === cancelPiece.dCd);

    // 일치하는 아이템이 있으면 드래그피스 배열 업데이트
    if (index !== -1) {
      // cancelPiece의 속성을 사용하여 새 항목을 생성하고 InDropZone을 false 설정
      const updatedItem = {
        ...copyDragPieces[index],
        ...cancelPiece,
        isInDropZone: false,
      };

      // 업데이트된 아이템으로 드래그피스 배열의 아이템 대치
      copyDragPieces[index] = updatedItem;
      setDragPieces(copyDragPieces); // 드래그피스배열 setState
    }
  };

  return (
    <DroppableCellStyle
      ref={drop}
      canDrop={canDrop}
      isOver={isOver}
      // onMouseDown={() => onClickRemove(cell)}
      style={{
        gridColumnStart: cell.location.column + 1,
        gridRowStart: cell.location.row + 1,
        gridColumnEnd: cell.location.column + cell.width + 1,
        gridRowEnd: cell.location.row + cell.height + 1,
        backgroundColor: isOver
          ? canDrop
            ? theme === 'light'
              ? light.selected_primary
              : dark.selected_primary
            : 'red'
          : cell.name === ''
          ? theme === 'light'
            ? light.board
            : dark.board
          : undefined,
      }}
    >
      <div style={cell.name === '' || (!auth.createAuth && !auth.updateAuth) ? { display: 'none' } : { display: 'flex' }}>
        <IoCloseCircleSharp className='closeIcon' onClick={() => onClickRemove(cell)} />
      </div>
      <div className='dropCell' style={cell.name === '' ? { display: 'none' } : { display: 'flex' }}>
        {children}
      </div>
    </DroppableCellStyle>
  );
};

const DashboardSetting = ({ dGubun }: { dGubun: 'h' | 's' | 'c' }) => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const size = useOutletContext<any>();
  const [reLocatedDragPieces, setReLocatedDragPieces] = useState<Piece[]>([]); // 위치 수정한 드래그피스 데이터(width 조절시 다시 되돌아가기 위한 state값)
  const [dropZoneGrid, setDropZoneGrid] = useState<any[]>([]);
  const [dropZoneMaxRowCount, setDropZoneMaxRowCount] = useState<number>(0);
  const [dashboardCdList, setDashboardCdList] = useState<any[]>([]);
  const [dashboardSetList, setDashboardSetList] = useState<IDashboard[]>([]);
  const [dashboardMList, setDashboardMList] = useState<Piece[]>([]);
  const [dragPieces, setDragPieces] = useState<Piece[]>([]);
  const [collapsedTypes, setCollapsedTypes] = useState<Record<string, boolean>>({ 날씨: false, 시계: true, 출역: true, 센서: true, '현장 정보': true, '안전 정보': true, 터널: true, 기타: true });

  // 토글 클릭 핸들러
  const handleToggleClick = (categoryKey: string) => {
    const typeKey = categoryKey;
    const newCollapsedTypes = { ...collapsedTypes };

    // 현재 클릭한 토글이 열려있는지 확인
    const isCurrentlyOpen = !newCollapsedTypes[typeKey];

    if (isCurrentlyOpen) {
      // 현재 열려있다면 닫기
      newCollapsedTypes[typeKey] = true;
    } else {
      // 현재 닫혀있다면 모든 토글을 닫고 해당 토글만 열기
      Object.keys(newCollapsedTypes).forEach((key) => {
        newCollapsedTypes[key] = true;
      });
      newCollapsedTypes[typeKey] = false;
    }

    setCollapsedTypes(newCollapsedTypes);
  };

  const dTypeLabel: Record<string, string> = useMemo(
    () =>
      DASHBOARD_TYPE_LIST.sort((a, b) => a.index - b.index) // index 순서로 정렬
        .reduce((acc, item) => {
          acc[item.category] = t(item.cdName);
          return acc;
        }, {} as Record<string, string>),
    [t]
  );

  const D_GUBUN_H = '1'; // 본사(사내)용 대시보드 구분 공통코드 목록 (etc1값)
  const D_GUBUN_S = '2'; // 현장용 대시보드 구분 공통코드 목록(etc1값)

  const {
    data: dGubunComCdData,
    isLoading: isLoadingDGubunComCdData,
    isFetching: isFetchingDGubunComCdData,
  } = GetFetchStatus({ path: '/code/detail', req: { grCd: DASHBOARD_GUBUN }, queryKey: dGubun });
  const {
    data: dashboardMListData,
    isLoading: isLoadingDashboardMListData,
    isFetching: isFetchingDashboardMListData,
  } = GetFetchStatus({ path: '/dashboard/master', req: { dGubun }, queryKey: dGubun });

  const {
    data: dashboardSetListData,
    isLoading: isLoadingDashboardSetListData,
    isFetching: isFetchingDashboardSetListData,
  } = GetFetchStatus({ path: '/dashboard', req: { dGubun }, queryKey: dGubun });

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '메인화면 관리 > DashBoard 설정',
      action: '조회',
      etc: ``,
    });
  }, []);

  useEffect(() => {
    if (dashboardSetListData) {
      const { DashboardSetList } = dashboardSetListData;
      let newArray = [];
      if (dGubun === 'h') {
        newArray = DashboardSetList.filter((el: any) => el.useYn === 'Y' && el.huseYn === 'Y');
      }
      if (dGubun === 's') {
        newArray = DashboardSetList.filter((el: any) => el.useYn === 'Y' && el.suseYn === 'Y');
      }
      if (dGubun === 'c') {
        newArray = DashboardSetList.filter((el: any) => el.useYn === 'Y' && el.cuseYn === 'Y');
      }
      setDashboardSetList(newArray);
    }
  }, [dashboardSetListData, dGubun]);

  useEffect(() => {
    if (dashboardSetList && dashboardMListData) {
      const { DashboardMList } = dashboardMListData;

      const useYDashboard = dashboardSetList.filter((setListEl: IDashboard) => setListEl.useYn === 'Y');
      const result = useYDashboard.map((setListEl: IDashboard) => {
        const dataItem = DashboardMList.find((mListEl: IDashboardM) => mListEl.dCd === setListEl.dCd);
        return {
          dCd: setListEl.dCd,
          name: setListEl.dName,
          isInDropZone: !!dataItem,
          ...(dataItem ? { location: { column: dataItem.dCol, row: dataItem.dRow } } : { location: { column: -1, row: -1 } }),
          width: setListEl.dCol,
          height: setListEl.dRow,
          cdSort: setListEl.cdSort,
          category: (setListEl as any).category || '기타',
        };
      });
      setDashboardMList(result);
    }
  }, [dashboardMListData, dashboardSetList]);

  useEffect(() => {
    if (dashboardMList?.length > 0) {
      setDragPieces(dashboardMList);
    }
  }, [dashboardMList]);

  useEffect(() => {
    if (dGubunComCdData) {
      const { comCdList } = dGubunComCdData;
      // 유저의 본사/현장/사내 선택여부 따라 구분목록 세팅
      let newArray = [];

      // 본사
      if (userInfo.hCd.substring(0, 1)?.toLowerCase() === 'h') {
        newArray = comCdList.filter(({ useYn, etc1 }: IComCdList) => useYn === 'Y' && etc1 === D_GUBUN_H).map(({ subCd, cdName, cdSort }: IComCd) => ({ subCd, cdName, cdSort }));
      }

      // 현장 또는 사내
      else if (userInfo.hCd.substring(0, 1)?.toLowerCase() === 's' || userInfo.hCd.substring(0, 1)?.toLowerCase() === 'c') {
        newArray = comCdList.filter(({ useYn, etc1 }: IComCdList) => useYn === 'Y' && etc1 === D_GUBUN_S).map(({ subCd, cdName }: IComCd) => ({ type: 'subCd', subCd, name: cdName }));
      }
      const sorted = arraySortByAscdOrder(newArray, 'cdSort');
      setDashboardCdList(sorted);
    }
  }, [dGubunComCdData]);

  const generateInitialGrid = (sizeParam: number) => {
    let maxRow = 0;
    const dropZonePieces = dragPieces.filter((el) => el.isInDropZone);
    const updatedDropZonePieces = addMissingPieces(dropZonePieces);

    dropZonePieces.forEach((piece) => {
      const endRow = piece.location.row + piece.height - 1;
      if (endRow > maxRow) maxRow = endRow;
    });
    setDropZoneMaxRowCount(maxRow + 1);
    return updatedDropZonePieces;
  };

  function addMissingPieces(dropZonePieces: any[]) {
    const dropPieces =
      dropZonePieces.length > 0
        ? dropZonePieces
        : [
            {
              dropZoneId: 0,
              name: '',
              isInDropZone: true,
              location: {
                row: 0,
                column: 0,
              },
              width: 1,
              height: 1,
            },
          ];

    let maxColumn = 0;
    let maxRow = 0;

    // 컬럼, 로우의 최대값 구하기
    dropPieces.forEach((piece: any) => {
      // maxColumn = Math.max(maxColumn, piece.location.column + piece.width);
      maxColumn = 4;
      maxRow = Math.max(maxRow, piece.location.row + piece.height + 2); // 드랍존 세로로는 무한대 추가할수 있기때문에 추가 드랍할 자리를 맨밑에 2칸더 깔아놓기(세로 최대값이 2이기때문)
    });

    // 2차배열 생성하고 false로 채우기
    const grid = Array(maxRow)
      .fill(false)
      .map(() => Array(maxColumn).fill(false));

    // 그리드에 사용된 셀을 표시
    dropPieces.forEach((piece: any) => {
      for (let i = 0; i < piece.height; i += 1) {
        for (let j = 0; j < piece.width; j += 1) {
          grid[piece.location.row + i][piece.location.column + j] = true;
        }
      }
    });

    // 그리드 상에 빈곳 채워넣기
    for (let row = 0; row < maxRow; row += 1) {
      for (let column = 0; column < maxColumn; column += 1) {
        if (!grid[row][column]) {
          dropPieces.push({
            dropZoneId: dropPieces.length,
            // name: `New Piece ${dropPieces.length}`,
            name: '',
            isInDropZone: true,
            location: {
              row,
              column,
            },
            width: 1,
            height: 1,
          });
        }
      }
    }

    return dropPieces;
  }

  useEffect(() => {
    const initialGrid = generateInitialGrid(4);
    const mergedGrid = initialGrid.map((gridItem: any) => {
      const matchPiece = dragPieces.find((piece) => piece.dCd === gridItem.dCd);
      return matchPiece ? { ...gridItem, ...matchPiece } : gridItem;
    });
    setDropZoneGrid(mergedGrid);
  }, [dragPieces]);

  const onClickSave = () => {
    const savePieces = dragPieces.filter((el: Piece) => el.isInDropZone);
    if (savePieces.length > 0) {
      const newArray = savePieces.map((el: Piece) => ({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        dGubun,
        dCd: el.dCd,
        dRow: el.location.row,
        dCol: el.location.column,
        editor: userInfo.userId,
      }));
      saveDashboardSettingAPI(newArray);
    } else {
      /**
       * 수정자 : 한영광
       * 수정일자 : 2023.10.16
       * 수정내용 : 데이터가 비어있는 경우 예외처리가 안돼있어서 추가함 (백엔드에서 데이터가 비어있는 경우를 구분할 수 있도록 dCd값을 0으로 보냄)
       */
      const newArray = [
        {
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          dGubun,
          dCd: '0',
          editor: userInfo.userId,
        },
      ];
      saveDashboardSettingAPI(newArray);
    }
  };

  const saveDashboardSettingAPI = async (array: any[]) => {
    const req = { dashboardMReqDto: array };
    const res = await apiPost({ path: '/dashboard/master', req });

    const { statusCode, message } = res.data;
    if (statusCode === 200) {
      toast.success(t(message));

      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: 'DashBoard 설정',
        action: `${dashboardCdList.filter((v: any) => v.subCd === dGubun)[0]?.name ?? ''} 저장`,
        etc: ``,
      });
    } else toast.warning(t(message));
  };

  const handleDrop = (dropItem: Piece, dropCell: any) => {
    const copyDragPieces = [...dragPieces];
    if (dropItem.dCd === '009') {
      const findObj = copyDragPieces.find((el) => el.dCd === '010' && el.isInDropZone);
      if (findObj !== undefined) {
        toast.info(t('소형/대형 중 하나의 유형만 배치 가능합니다'));
        return;
      }
    }

    if (dropItem.dCd === '010') {
      const findObj = copyDragPieces.find((el) => el.dCd === '009' && el.isInDropZone);
      if (findObj !== undefined) {
        toast.info(t('소형/대형 중 하나의 유형만 배치 가능합니다'));
        return;
      }
    }

    if (dropItem.dCd === '011') {
      const findObj = copyDragPieces.find((el) => el.dCd === '012' && el.isInDropZone);
      if (findObj !== undefined) {
        toast.info(t('소형/대형 중 하나의 유형만 배치 가능합니다'));
        return;
      }
    }

    if (dropItem.dCd === '012') {
      const findObj = copyDragPieces.find((el) => el.dCd === '011' && el.isInDropZone);
      if (findObj !== undefined) {
        toast.info(t('소형/대형 중 하나의 유형만 배치 가능합니다'));
        return;
      }
    }

    if (dropItem.dCd === '096') {
      const findObj = copyDragPieces.find((el) => el.dCd === '097' && el.isInDropZone);
      if (findObj !== undefined) {
        toast.info(t('소형/대형 중 하나의 유형만 배치 가능합니다'));
        return;
      }
    }

    if (dropItem.dCd === '097') {
      const findObj = copyDragPieces.find((el) => el.dCd === '096' && el.isInDropZone);
      if (findObj !== undefined) {
        toast.info(t('소형/대형 중 하나의 유형만 배치 가능합니다'));
        return;
      }
    }

    /**
     * 수정자 : 한영광
     * 수정일자 : 2023.10.16
     * 수정내용 : 아이템 이동 시 겹치는 부분이 생기는 문제가 있어 아이템 사이즈 만큼 점유된 셀 체크를 하도록 수정함
     */
    // 이미 점유된 셀에 드랍시도할 경우 드랍취소
    const dropItemBeforeLocation: any[] = []; // dropItemBeforeLocation는 drop한 item의 기존 location을 담을 변수
    for (let x = dropItem.location.column; x < dropItem.location.column + dropItem.width; x += 1) {
      for (let y = dropItem.location.row; y < dropItem.location.row + dropItem.height; y += 1) {
        dropItemBeforeLocation.push({ x, y });
      }
    }
    let dropCheck = false; // dropCheck가 true일때 드랍 취소
    const newDropZoneGrid = dropZoneGrid.filter((el) => !el.dCd);
    if (dropCell.dCd) {
      dropCheck = true;
    } else {
      for (let x = dropCell.location.column; x < dropCell.location.column + dropItem.width; x += 1) {
        for (let y = dropCell.location.row; y < dropCell.location.row + dropItem.height; y += 1) {
          let check = false;
          newDropZoneGrid.forEach((el: any) => {
            // newDropZoneGrid는 비어있는 셀 리스트
            if (el.location.column === x && el.location.row === y) {
              check = true; // check가 true일 때 해당 셀은 드랍 가능
            }
            dropItemBeforeLocation.map((data: any) => {
              if (data.x === x && data.y === y) {
                check = true; // check가 true일 때 해당 셀은 드랍 가능
              }
            });
          });
          if (!check) {
            dropCheck = true; // dropCheck가 true일때 드랍 취소
          }
        }
      }
    }
    if (dropCheck) {
      // 드랍취소
      if (dropItem.isInDropZone) {
        // dropItem의 id와 일치하는 항목을 드래그 피스 배열에서 찾기
        const index = copyDragPieces.findIndex((piece) => piece.dCd === dropItem.dCd);

        // 일치하는 아이템이 있으면 드래그피스 배열 업데이트
        if (index !== -1) {
          // dropItem의 속성을 사용하여 새 항목을 생성하고 InDropZone을 true로 설정
          const updatedItem = {
            ...copyDragPieces[index],
            ...dropItem,
            isInDropZone: true,
            location: dropItem.location,
          };
          // 업데이트된 아이템으로 드래그피스 배열의 아이템 대치
          copyDragPieces[index] = updatedItem;
          setDragPieces(copyDragPieces); // 드래그피스배열 setState
          setReLocatedDragPieces(copyDragPieces);
        }
      }
      return;
    }
    // dropItem의 id와 일치하는 항목을 드래그 피스 배열에서 찾기
    const index = copyDragPieces.findIndex((piece) => piece.dCd === dropItem.dCd);

    // 일치하는 아이템이 있으면 드래그피스 배열 업데이트
    if (index !== -1) {
      // dropItem의 속성을 사용하여 새 항목을 생성하고 InDropZone을 true로 설정
      const updatedItem = {
        ...copyDragPieces[index],
        ...dropItem,
        isInDropZone: true,
        location: dropCell.location,
      };
      // 업데이트된 아이템으로 드래그피스 배열의 아이템 대치
      copyDragPieces[index] = updatedItem;
      setDragPieces(copyDragPieces); // 드래그피스배열 setState
      setReLocatedDragPieces(copyDragPieces);
    }
  };

  const sortedDragPiecies: Piece[] = dragPieces.sort((a, b) => {
    // 로우 오름차순으로 sorting
    if (a.location.row < b.location.row) return -1;
    if (a.location.row > b.location.row) return 1;

    // 로우값이 같을때, 컬럼 오름차순으로 sorting
    if (a.location.column < b.location.column) return -1;
    if (a.location.column > b.location.column) return 1;

    // 로우값과 컬럼값이 동일할때
    return 0;
  });

  useEffect(() => {
    if (reLocatedDragPieces.length === 0) {
      setReLocatedDragPieces(dragPieces);
    }
  }, [dragPieces]);

  useEffect(() => {
    const updateDragPiecesForMobile = () => {
      // 가로 사이즈가 1024 미만으로 줄어들때, 컬럼을 통합하고 로우 오름차순, 컬럼 오름차순으로 정렬순서 객체에 넣기
      let mRowCounter = 0;
      return sortedDragPiecies.map((piece) => {
        const { height } = piece;
        const updatedPiece = {
          ...piece,
          location: { row: mRowCounter, column: 0 },
        };
        mRowCounter += height;
        return updatedPiece;
      });
    };

    if (size.innerSize.W <= 1024) {
      const updatedDragPiecies = updateDragPiecesForMobile();
      setDragPieces(updatedDragPiecies);
    } else if (reLocatedDragPieces.length > 0) {
      // 가로 사이즈 1024 이상일 때 기존 피스 location대로 다시 배치
      setDragPieces(reLocatedDragPieces);
    }
  }, [size]);

  const loading =
    isLoadingDashboardSetListData || isFetchingDashboardSetListData || isLoadingDGubunComCdData || isFetchingDGubunComCdData || isLoadingDashboardMListData || isFetchingDashboardMListData;

  if (loading) return null;
  return (
    <Root loading={loading} maxRowCount={dropZoneMaxRowCount} colCount={4}>
      <div className='main'>
        <DndProvider backend={HTML5Backend}>
          <div className='dragNDropZone'>
            <div className='dragZone'>
              {DASHBOARD_TYPE_LIST.sort((a, b) => a.index - b.index).map((typeItem) => {
                const typeKey = typeItem.category;
                const items = dragPieces.filter((piece) => (piece as any).category === typeKey);
                const categoryKey = typeKey;
                const collapsed = collapsedTypes[categoryKey] === true;
                const visibleItems = (items as Piece[])
                  .filter((el) => !el.isInDropZone)
                  .sort((a, b) => {
                    if (a.cdSort > b.cdSort) return 1;
                    if (a.cdSort < b.cdSort) return -1;
                    return 0;
                  });

                return (
                  <div key={`group-${typeKey}`} className='drag-group'>
                    <div className='drag-group-header' onClick={() => handleToggleClick(categoryKey)} role='button' tabIndex={0} onKeyDown={() => handleToggleClick(categoryKey)}>
                      <span className='material-symbols-rounded expandIcon'>expand_{`${collapsed ? 'less' : 'more'}`}</span>
                      {dTypeLabel[categoryKey]}
                    </div>
                    <div className={`drag-group-body ${!collapsed ? 'expanded' : ''}`}>
                      {visibleItems.length > 0 ? (
                        visibleItems.map((el, i) => <DraggableCell key={`${typeKey}-${i}-${el.dCd ?? i}`} cell={el} dragPieces={dragPieces} setDragPieces={setDragPieces} />)
                      ) : (
                        <div style={{ padding: '10px', textAlign: 'center', color: '#999', fontSize: '14px' }}>{t('사용 가능한 항목이 없습니다')}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className='dropZone'>
              {dropZoneGrid?.map(
                (el, i) =>
                  el.isInDropZone && (
                    <DroppableZone
                      key={`${el.dCd}_${i}`}
                      onDrop={(item: Piece) => handleDrop(item, el)}
                      cell={el}
                      dragPieces={dragPieces}
                      setDragPieces={setDragPieces}
                      dropZoneGrid={dropZoneGrid}
                      setDropZoneGrid={setDropZoneGrid}
                      auth={auth}
                    >
                      <DraggableCell key={el.dCd} cell={el} dragPieces={dragPieces} setDragPieces={setDragPieces} />
                    </DroppableZone>
                  )
              )}
            </div>
          </div>
        </DndProvider>
      </div>
      {(auth.createAuth || auth.updateAuth) && (
        <ButtonsWrapper>
          <BtnBlue type='button' onClick={onClickSave}>
            {t('저장')}
          </BtnBlue>
        </ButtonsWrapper>
      )}
    </Root>
  );
};

export default DashboardSetting;
